/** Этапы AI-пайплайна (ядро), управляемые флагами профиля. */
import { PIPELINE_TOGGLE_STAGES, WINPROB_PIPELINE_META } from '@/billing/pipelineStages'

export const ANALYSIS_PIPELINE_STAGES = [
  {
    key: 'criteria',
    label: 'Критерии',
    flag: 'pipeline_allow_tender_criteria',
    startField: 'criteria_started_at',
    finishField: 'criteria_finished_at',
  },
  {
    key: 'items',
    label: 'Позиции',
    flag: 'pipeline_allow_items_analysis',
    startField: 'items_started_at',
    finishField: 'items_finished_at',
  },
]

/** Доп. этапы phase2 (автозапуск при analyze). */
export const PHASE2_PIPELINE_STAGES = [
  {
    key: 'compliance',
    label: 'Антириск-скан',
    flag: 'pipeline_allow_compliance_scan',
  },
  {
    key: 'approval_roadmap',
    label: 'Дорожная карта',
    flag: 'pipeline_allow_approval_roadmap',
  },
]

export const WINPROB_STAGE = {
  key: WINPROB_PIPELINE_META.stageKey || 'winprob',
  label: WINPROB_PIPELINE_META.label,
  startField: 'winprob_started_at',
  finishField: 'winprob_finished_at',
}

const ALL_STAGE_DEFS = [
  ...ANALYSIS_PIPELINE_STAGES,
  WINPROB_STAGE,
  ...PHASE2_PIPELINE_STAGES,
]

const PROGRESS_LABELS = {
  running: 'Выполняется',
  criteria_done: 'Критерии готовы',
  items_done: 'Позиции готовы',
  analyzed: 'Завершено',
  failed: 'Ошибка',
}

const STAGE_STATUS_LABELS = {
  pending: 'Ожидание',
  running: 'Выполняется',
  done: 'Готово',
  skipped: 'Пропущено',
  failed: 'Ошибка',
}

const TERMINAL = new Set(['done', 'skipped', 'failed'])

export function getStageStatusLabel(status) {
  return STAGE_STATUS_LABELS[status] || status
}

export function getProgressLabel(progress) {
  const p = (progress || '').toLowerCase()
  return PROGRESS_LABELS[p] || (progress || '—')
}

export function isPipelineFlagEnabled(user, flag) {
  if (!flag) return true
  return user?.[flag] !== false
}

function stageDefByKey(key) {
  return ALL_STAGE_DEFS.find((s) => s.key === key)
}

/** Включённые этапы кольца (все кроме chat). */
export function getEnabledPipelineStages(user) {
  const out = []
  for (const s of ANALYSIS_PIPELINE_STAGES) {
    if (isPipelineFlagEnabled(user, s.flag)) out.push(s)
  }
  out.push(WINPROB_STAGE)
  for (const s of PHASE2_PIPELINE_STAGES) {
    if (isPipelineFlagEnabled(user, s.flag)) out.push(s)
  }
  return out.length ? out : [ANALYSIS_PIPELINE_STAGES[0], WINPROB_STAGE]
}

function normalizeProgress(row) {
  return (row?.progress || '').toLowerCase()
}

function collectStageEntries(row) {
  const lists = [
    Array.isArray(row?.pipeline_stages) ? row.pipeline_stages : [],
    Array.isArray(row?.stages) ? row.stages : [],
    Array.isArray(row?.breakdown) ? row.breakdown : [],
    Array.isArray(row?.billing?.stages) ? row.billing.stages : [],
  ]
  const map = new Map()
  for (const list of lists) {
    for (const entry of list) {
      if (!entry || typeof entry !== 'object') continue
      const key = entry.stage || entry.key
      if (key) map.set(String(key), entry)
    }
  }
  return map
}

function isStageEntryDone(entry) {
  if (!entry || typeof entry !== 'object') return false
  if (entry.status === 'done' || entry.status === 'skipped') return true
  if (entry.finished_at || entry.finishedAt) return true
  const duration = entry.duration_seconds ?? entry.duration
  if (duration != null && Number(duration) > 0) return true
  const cost = entry.cost_rub ?? entry.cost ?? entry.total_cost
  if (cost != null && Number(cost) > 0) return true
  if (entry.total_tokens != null && Number(entry.total_tokens) > 0) return true
  return false
}

function isTimestampStageDone(row, stageDef) {
  if (!stageDef?.finishField) return false
  return Boolean(row?.[stageDef.finishField])
}

function isTimestampStageRunning(row, stageDef) {
  if (!stageDef) return false
  const started = row?.[stageDef.startField]
  const finished = row?.[stageDef.finishField]
  return Boolean(started && !finished)
}

function resolveStatusFromApi(entry) {
  const raw = (entry?.status || '').toLowerCase()
  if (raw && STAGE_STATUS_LABELS[raw]) return raw
  if (isStageEntryDone(entry)) return 'done'
  return 'pending'
}

function isStageDone(row, stageDef, stageEntries) {
  const entry = stageEntries.get(stageDef.key)
  if (entry && entry.status) {
    return entry.status === 'done' || entry.status === 'skipped'
  }
  return isStageEntryDone(entry) || isTimestampStageDone(row, stageDef)
}

function resolveStageStatus(row, stageDef, enabled, progress, enabledKeys, stageEntries) {
  if (!enabled) return 'skipped'

  const entry = stageEntries.get(stageDef.key)
  if (entry?.status) {
    const apiStatus = resolveStatusFromApi(entry)
    if (apiStatus !== 'pending' || !isStageEntryDone(entry)) {
      if (apiStatus === 'pending' && isTimestampStageRunning(row, stageDef)) return 'running'
      return apiStatus
    }
  }

  if (isStageDone(row, stageDef, stageEntries)) return 'done'
  if (isTimestampStageRunning(row, stageDef)) return 'running'

  const p = normalizeProgress(row)
  const stageIndex = enabledKeys.indexOf(stageDef.key)

  if (p === 'failed') {
    const firstIncomplete = enabledKeys.findIndex((key) => {
      const def = stageDefByKey(key)
      return def && !isStageDone(row, def, stageEntries)
    })
    if (firstIncomplete === -1) return 'done'
    if (stageIndex === firstIncomplete) return 'failed'
    if (stageIndex < firstIncomplete) return 'done'
    return 'pending'
  }

  const completed = progressToCompletedCount(p, enabledKeys)
  if (stageIndex < completed) return 'done'
  if (stageIndex === completed && ['running', 'criteria_done', 'items_done'].includes(p)) {
    return 'running'
  }
  return 'pending'
}

export function progressToCompletedCount(progress, enabledKeys) {
  const p = (progress || '').toLowerCase()
  const keys = enabledKeys?.length ? enabledKeys : ['criteria']
  const n = keys.length

  if (p === 'failed' || !p) return 0
  if (p === 'analyzed') return n
  if (p === 'items_done') return Math.max(0, n - 1)
  if (p === 'criteria_done') return Math.min(1, n)
  if (p === 'running') return 0
  return 0
}

function buildStagesList(row, user) {
  const enabledStages = getEnabledPipelineStages(user)
  const enabledKeys = enabledStages.map((s) => s.key)
  const progress = normalizeProgress(row)
  const stageEntries = collectStageEntries(row)

  return ALL_STAGE_DEFS.map((stageDef) => {
    const enabled = enabledKeys.includes(stageDef.key)
    const status = resolveStageStatus(
      row,
      stageDef,
      enabled,
      progress,
      enabledKeys,
      stageEntries,
    )
    return {
      key: stageDef.key,
      label: stageDef.label,
      status,
      statusLabel: getStageStatusLabel(status),
      ringSector: enabled,
    }
  })
}

export function isPipelineComplete(row, user) {
  if (row?.pipeline_complete === true) return true
  if (row?.pipeline_complete === false) return false
  const stages = buildStagesList(row, user).filter((s) => s.ringSector)
  if (!stages.length) return false
  return stages.every((s) => TERMINAL.has(s.status))
}

export function isPipelineRunning(row, user) {
  if (row?.pipeline_running === true) return true
  if (row?.pipeline_running === false && row?.pipeline_complete === true) return false
  const p = normalizeProgress(row)
  if (p === 'failed') return false
  if (isPipelineComplete(row, user)) return false
  const stages = buildStagesList(row, user).filter((s) => s.ringSector)
  if (stages.some((s) => s.status === 'running' || s.status === 'pending')) return true
  return ['running', 'criteria_done', 'items_done'].includes(p)
}

/**
 * @param {object} row — строка history или объект анализа
 * @param {object} [user] — auth.user
 */
export function resolveAnalysisProgress(row, user) {
  const progress = normalizeProgress(row)
  const enabledStages = getEnabledPipelineStages(user)
  const enabledKeys = enabledStages.map((s) => s.key)
  const ringStages = enabledStages
  const totalSectors = ringStages.length
  const stageEntries = collectStageEntries(row)

  const stages = buildStagesList(row, user)

  let completedSectors = 0
  for (const stage of ringStages) {
    const st = stages.find((x) => x.key === stage.key)
    if (st?.status === 'done') completedSectors += 1
  }

  if (completedSectors === 0 && progress) {
    completedSectors = progressToCompletedCount(progress, enabledKeys)
  }

  if (isPipelineComplete(row, user)) {
    completedSectors = totalSectors
  }

  completedSectors = Math.min(Math.max(0, completedSectors), totalSectors)

  let activeSectorIndex = -1
  const runningIdx = ringStages.findIndex((s) => {
    const st = stages.find((x) => x.key === s.key)
    return st?.status === 'running'
  })
  if (runningIdx >= 0) {
    activeSectorIndex = runningIdx
  } else if (['running', 'criteria_done', 'items_done'].includes(progress)) {
    activeSectorIndex = Math.min(completedSectors, totalSectors - 1)
  }

  let state = 'idle'
  if (progress === 'failed' || stages.some((s) => s.ringSector && s.status === 'failed')) {
    state = 'failed'
  } else if (isPipelineComplete(row, user)) {
    state = 'done'
  } else if (isPipelineRunning(row, user)) {
    state = 'active'
  }

  const statusLabel = isPipelineComplete(row, user)
    ? 'Завершено'
    : getProgressLabel(progress)

  return {
    totalSectors,
    completedSectors,
    activeSectorIndex,
    state,
    statusLabel,
    progress,
    stages,
    pipelineComplete: isPipelineComplete(row, user),
    pipelineRunning: isPipelineRunning(row, user),
  }
}

function stageSignature(stage) {
  if (!stage) return ''
  return `${stage.key}:${stage.status}:${stage.statusLabel}`
}

/**
 * Обновляет объект прогресса на месте (для таблицы списков без пересоздания строки).
 * @returns {boolean} были ли изменения
 */
export function patchAnalysisProgressInPlace(existing, next) {
  if (!next) return false
  if (!existing) return false

  let changed = false
  const scalarKeys = [
    'totalSectors',
    'completedSectors',
    'activeSectorIndex',
    'state',
    'statusLabel',
    'progress',
    'pipelineComplete',
    'pipelineRunning',
  ]
  for (const key of scalarKeys) {
    if (existing[key] !== next[key]) {
      existing[key] = next[key]
      changed = true
    }
  }

  const prevStages = Array.isArray(existing.stages) ? existing.stages : []
  const nextStages = Array.isArray(next.stages) ? next.stages : []
  if (prevStages.length !== nextStages.length) {
    existing.stages = nextStages.map((s) => ({ ...s }))
    return true
  }
  for (let i = 0; i < nextStages.length; i += 1) {
    if (stageSignature(prevStages[i]) !== stageSignature(nextStages[i])) {
      existing.stages = nextStages.map((s) => ({ ...s }))
      changed = true
      break
    }
  }

  return changed
}
